/**
 * @fileoverview Extended Vue Router with improved navigation failure handling.
 *
 * Patches the Router.push method to gracefully handle navigation failures
 * (e.g., duplicate navigation) without throwing unhandled promise rejections.
 *
 * @module helpers/routing
 * @see {@link https://github.com/vuejs/vue-router/issues/2881} for issue reference
 */

import Router, { RawLocation, Route } from "vue-router";
import { ErrorHandler } from "vue-router/types/router";

/** @type {Function} Original Router.push method before patching */
const originalPush = Router.prototype.push;

/**
 * Patched Router.push that handles navigation failures gracefully.
 *
 * When navigation fails due to duplicate navigation or other NavigationFailure
 * errors, this patched method resolves instead of rejecting, preventing
 * "NavigationDuplicated" errors from causing unhandled promise rejections.
 *
 * @param {RawLocation} location - Target route location
 * @param {Function} [onResolve] - Success callback (optional)
 * @param {ErrorHandler} [onReject] - Error callback (optional)
 * @returns {Promise<Route>|void} Route promise or void if callbacks provided
 */
// @ts-ignore
Router.prototype.push = function push(
  location: RawLocation,
  onResolve: Function | undefined,
  onReject: ErrorHandler | undefined
): Promise<Route> | void {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject);
  }
  // @ts-ignore
  return originalPush.call(this, location).catch((err) => {
    if (Router.isNavigationFailure(err)) {
      // Navigation failures are expected (e.g., duplicate nav) - resolve them
      return err;
    }
    // Rethrow actual errors
    return Promise.reject(err);
  });
};

export default Router;
